import { useState, useEffect } from "react";
import {
  Box,
  // Card,
  // Table,
  // TableBody,
  // TableContainer,
  // TableCell,
  // TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  // TextareaAutosize,
  DialogActions,
  Button,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// import { format } from "date-fns";
import * as consts from "../constants";
import { IOrderInfo } from "../slices/authSlice";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import {
  getOrdersByCustomerId,
  createComment,
  updateInfo,
} from "../slices/authSlice";
import {
  showNotification,
  NotificationType,
} from "../slices/notificationSlice";
import { getOrderStatusText, getServiceTypeText } from "../utils/orderUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 配置 dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

export function CustomerOrdersView() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);
  const orders = useAppSelector((state) => state.auth.orders);
  const [loading, setLoading] = useState(true);

  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrderInfo | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (basicUserInfo) {
      setLoading(true);
      dispatch(
        getOrdersByCustomerId({
          customer_id: +basicUserInfo.uid,
          count: 200,
          needComments: true,
        })
      )
        .then(() => {
          setLoading(false); // 加载完成
        })
        .catch(() => {
          setLoading(false); // 加载出错时也要设置为 false
        });
    } else {
      setLoading(false); // 如果没有 basicUserInfo，也设置为 false
    }
  }, []);

  if (!basicUserInfo || basicUserInfo.status === consts.customerStatus.BAN) {
    return <Navigate replace to={"/login"} />;
  }

  const handleCommentService = (order: IOrderInfo) => {
    setSelectedOrder(order);
    setCommentDialogOpen(true);
  };

  const handleCommentDialogClose = () => {
    setCommentDialogOpen(false);
    setSelectedOrder(null);
    setComment("");
  };

  const handleCommentSubmit = async () => {
    if (!selectedOrder) {
      // console.error("No order selected.");
      return;
    }

    const result = await dispatch(
      createComment({
        order_id: selectedOrder.order_id,
        customer_id: +basicUserInfo.uid,
        driver_id: selectedOrder.driver || 0, // 如果driver还未指定
        customer_name: basicUserInfo.name,
        driver_name: selectedOrder.driver_name || "未指定", // 如果driver_name还未指定
        desc: comment,
        type: consts.commentType.CUSTOMER_PROVIDED,
        isClient: true,
      })
    );

    if (createComment.fulfilled.match(result)) {
      // 成功
      dispatch(
        showNotification({
          message: t("msgComentSuccess"),
          type: NotificationType.Success,
        })
      );
      // 关闭对话框
      // setCommentDialogOpen(false);
      // setComment("");
      // setSelectedOrder(null);
      handleCommentDialogClose();
    } else {
      // 失败
      dispatch(
        showNotification({
          message: t("msgComentFailed"),
          type: NotificationType.Error,
        })
      );
    }
  };

  const handleDriverPreference = async (driverId: number, isLike: boolean) => {
    if (!basicUserInfo) return;

    try {
      const result = await dispatch(
        updateInfo({
          customer_id: +basicUserInfo.uid,
          [isLike ? "prefer_driver" : "dislike_driver"]: driverId,
        })
      );

      // 检查 action 是否成功完成
      if (updateInfo.fulfilled.match(result)) {
        dispatch(
          showNotification({
            message: t(isLike ? "msgLikeSuccess" : "msgUnlikeSuccess"),
            type: NotificationType.Success,
          })
        );
        // 刷新订单列表以获取最新状态
        dispatch(
          getOrdersByCustomerId({
            customer_id: +basicUserInfo.uid,
            count: 200,
            needComments: true,
          })
        );
      } else {
        // action 被拒绝
        dispatch(
          showNotification({
            message: t("msgPreferenceError"),
            type: NotificationType.Error,
          })
        );
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: t("msgPreferenceError"),
          type: NotificationType.Error,
        })
      );
    }
  };

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 1,
              color: "primary.main",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{t("btnHistory")}</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ mt: 4 }}>
            {t("labelNoHistoryOrders")}
          </Typography>
        ) : (
          <List>
            {orders.map((order) => {
              // 从flight_time或use_time中获取日期
              const orderDate = order.flight_time || order.use_time;
              const formattedDate = dayjs(orderDate)
                .tz("America/Los_Angeles")
                .format("MM/DD/YYYY");

              return (
                <ListItem
                  key={order.order_id}
                  divider
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate("/curorder", { state: { orderDetail: order } })
                  }
                >
                  <ListItemText
                    primary={
                      <Typography>
                        {formattedDate} - [Order #{order.order_id}]
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {order.service_type ===
                        consts.serviceType.AIRPORT_PICK_UP
                          ? t("pickupService")
                          : t("dropoffService")}
                        {" - "}
                        {getOrderStatusText(order.status, t)}
                        {order.status === consts.orderStatus.COMPLETED 
                        && !order.comment && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="primary" 
                            sx={{ ml: 2 }}
                            onClick={(e) => {
                              e.stopPropagation(); // 阻止事件冒泡
                              handleCommentService(order);
                            }}
                          >
                            {t("btnCommentService")}
                          </Button>
                        )}
                      </Typography>
                    }
                  />
                  <ChevronRightIcon color="action" />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
      {/* 评论对话框 */}
      <Dialog open={commentDialogOpen} onClose={handleCommentDialogClose}>
        <DialogTitle>{t("dialogTitleComment")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("dialogDescComment")}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            label={t("labelComment")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommentDialogClose}>{t("btnCancel")}</Button>
          <Button 
            onClick={handleCommentSubmit} 
            variant="contained" 
            color="primary"
            disabled={!comment.trim()}
          >
            {t("btnSubmit")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
